import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { CartResultLineItemSelection } from 'entities/cart-result';
import { VendorPartPriceResult } from 'entities/hot-flag/get-vendor-part-prices-result';
import { HotFlagResult } from 'entities/hot-flag/hot-flag-result';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map, tap, withLatestFrom } from 'rxjs/operators';
import { FeatureFlagService } from 'services/feature-flag.service';
import { AppState } from 'store/app-state';
import * as BackCounterRequestActions from 'store/back-counter/back-counter.actions';
import { OpeningFrom, RequestOrderItem } from 'store/back-counter/back-counter.entities';
import * as CartActions from 'store/cart/cart.actions';
import * as CartSelectors from 'store/cart/cart.selectors';
import * as FeatureFlagSelectors from 'store/feature-flags/feature-flags.selectors';
import * as MyCartActions from 'store/my-cart/my-cart.actions';
import * as MyCartSelectors from 'store/my-cart/my-cart.selectors';

type CartItemFormGroup = {
    cartItemId: FormControl<string | null>;
    hotFlagCode: FormControl<string>;
    bctId: FormControl<string | null>;
    esn: FormControl<string | null>;
    crc: FormControl<string | null>;
};

type HotFlagFormGroup = {
    vin: FormControl<string | null>;
    cartItems: FormArray<FormGroup<CartItemFormGroup>>;
};

@Component({
    selector: 'hot-flag',
    templateUrl: './hot-flag.component.html',
    standalone: false
})
export class HotFlagComponent implements OnInit, OnDestroy {
    HOT_FLAG_CODE_WARRANTY = "21";
    CUMMINS_VENDORS = [ "220980", "242190" ];

    @Input() public editHotFlagCode?: string;
    @Input() openingFrom: OpeningFrom;
    @Input() requestOrderItems: RequestOrderItem[];
    @Input() orderNumber: string;
    @Input() requestBranchCode: string;
    @Input() esn?: string;

    form: FormGroup<HotFlagFormGroup> = this.fb.group({
        vin: new FormControl<string>('', []),
        cartItems: new FormArray([])
    });

    vinValidators = [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(/^[a-zA-Z0-9]*$/)
    ];

    esnValidators = [
        Validators.minLength(8),
        Validators.required
    ];

    private filterSubject = new BehaviorSubject<string>('');
    filterChangeAction$: Observable<string> = this.filterSubject.asObservable();

    hotFlags$: Observable<HotFlagResult[]> = this.store.select(MyCartSelectors.hotFlags);
    vinHotFlagCodes$: Observable<string[]> = this.hotFlags$.pipe(
        map((hotFlags) => hotFlags.filter(hf => hf.isVINRequired === true).map(hf => hf.code)),
        tap((hotFlags) => this.vinHotFlagCodes = hotFlags)
    );
    cartVin: Subscription = this.store.select(CartSelectors.selectVin).pipe(
        tap((vin) => {
            if(this.openingFrom === 'CART'){
                this.form.patchValue({vin});
            }
        })
    ).subscribe();
    selectedCartItems$: Observable<CartResultLineItemSelection[]> = this.store.select(MyCartSelectors.selectedCartItems).pipe(
        tap((selectedCartItems) => {
            if (selectedCartItems.length === 0 && this.openingFrom === 'CART') {
                this.activeModal.close();
            }
        })
    );

    cartItemsWithVendorPrices$ = this.store.select(MyCartSelectors.vendorPartsPriceResult).pipe(tap(t => {
        this.requestOrderItems = this.requestOrderItems?.map(m => {
            const item = t.find(f => f.partNumber === m.materialNumber);
            if(item) {
                return {
                    ...m,
                    vendorCode: item?.vendorNumber ??''
                }
            } else {
                return m;
            }
        })
    }));

  removedItemBctId = "";

  filteredCartItems$: Observable<CartResultLineItemSelection[]> = combineLatest(
    [
      this.selectedCartItems$,
      this.filterChangeAction$,
      this.cartItemsWithVendorPrices$,
    ]
  ).pipe(
    map(
      ([selectedCartItems, filterString, _]: [
        CartResultLineItemSelection[],
        string,
        VendorPartPriceResult[]
      ]) =>
        selectedCartItems.filter((selectedCartItem) => {
          if (filterString === "") {
            return true;
          }
          return (
            selectedCartItem.partNumber
              ?.toLowerCase()
              .includes(filterString.toLocaleLowerCase()) ||
            selectedCartItem.corePartNumber
              ?.toLowerCase()
              .includes(filterString.toLocaleLowerCase())
          );
        })
    ),
    withLatestFrom(this.vinHotFlagCodes$, this.cartItemsWithVendorPrices$),
    tap(
      ([selectedCartItems, _]: [
        CartResultLineItemSelection[],
        string[],
        VendorPartPriceResult[]
      ]) => {
        if (this.openingFrom === "CART") {
          this.initForm(selectedCartItems);
        } else {
          this.requestOrderItems = this.requestOrderItems?.filter(
            (roi) => roi._id !== this.removedItemBctId
          );
          this.initForm(this.requestOrderItems);
        }
      }
    ),
    map(
      ([selectedCartItems, _, vendorPartPriceResult]: [
        CartResultLineItemSelection[],
        string[],
        VendorPartPriceResult[]
      ]) => {
        if (this.openingFrom === "CART") {
          this.selectedCartItemsLength = selectedCartItems?.length;
          return selectedCartItems;
        } else {
          this.selectedCartItemsLength = this.requestOrderItems.length;
          const cartResultLineItemSelections = this.requestOrderItems.map(
            (m) => {
              return {
                partNumber: m.materialNumber,
                bctId: m._id,
                vendorCode:
                  vendorPartPriceResult.find(
                    (f) => f.partNumber === m.materialNumber
                  )?.vendorNumber ?? "",
              } as CartResultLineItemSelection;
            }
          );
          return cartResultLineItemSelections;
        }
      }
    )
  );

    selectedCartItemsLength = 0;
    filterKey: string = '';
    partNumber: string = '';

    fixedVendor$: Observable<boolean> = this.store.select(MyCartSelectors.selectedCartItems).pipe(
        map(selectedItems => !selectedItems.some(selected => selected.vendorCode?.length === 0))
    );
    isHotFlagVinEnabled$ = this.store.select(FeatureFlagSelectors.isFeatureActive('Hotflag.VinNumber'));
    lastExpandedIndex: number = null;
    vinHotFlagCodes: string[] = [];
    subscription: Subscription;
    isVINUpdating: boolean = false;

    vinUpdateSubject = new Subject<{event: InputEvent, index: number, vendorCode: string}>();
    esnUpdateSubject = new Subject<{event: InputEvent, id: string, index: number}>();
    crcUpdateSubject = new Subject<{event: InputEvent, id: string, index: number}>();

    vinUpdate = this.vinUpdateSubject.pipe(
        tap(() => this.isVINUpdating = true),
        debounceTime(250),
        tap(({ event, index, vendorCode }) => {
            this.focusElementId = (event.target as HTMLElement).id;
                        
            // Always update the VIN error state regardless of form validity
            this.setVINError();
            
            if (!this.form.controls.vin.valid) {
                // Trigger change detection to ensure error state is updated in UI
                this.changeDetectionRef.detectChanges();
                return;
            }
            
            if(this.openingFrom === 'CART') {
                this.store.dispatch(CartActions.updateVIN({ vin: this.form.controls.vin.value }));
            }

            this.form.controls.cartItems.controls.forEach((g: FormGroup<CartItemFormGroup>) => {
                if (this.isVINRequiredForHotFlag(g.controls.hotFlagCode.value)) {
                    this.store.dispatch(MyCartActions.updateCartItemHotFlag({
                        cartItemId: g.controls.cartItemId.value,
                        hotFlagCode: g.controls.hotFlagCode.value,
                    }));
                }
            });
        }),
        tap(() => this.isVINUpdating = false)
    ).subscribe();

    esnUpdate = this.esnUpdateSubject.pipe(
        debounceTime(250),
        tap(({ id, index }) => {
            this.focusElementId = `hotFlag_esn_cartItemHotFlag_${index}`;
            if (!this.form.controls.cartItems.controls[index].controls.esn.valid) {
                this.setESNError();
                return;
            }
            this.store.dispatch(MyCartActions.updateCartItemESN({ cartItemId: id, esn: this.form.value.cartItems[index].esn }));
        })
    ).subscribe();

    crcUpdate = this.crcUpdateSubject.pipe(
        debounceTime(250),
        tap(({ id, index }) => {
            this.focusElementId = `hotFlag_crc_cartItemHotFlag_${index}`;
            if (!this.form.controls.cartItems.controls[index].controls.crc.valid) {
                return;
            }
            this.store.dispatch(MyCartActions.updateCartItemCRC({ cartItemId: id, crc: this.form.value.cartItems[index].crc }));
        })
    ).subscribe();

    focusElementId: string;
    vinControlSub: Subscription;
    showVINControl: boolean = false;
    showESNWithCRC: boolean = false;
    hasVINError: boolean = false;
    hasESNError: boolean = false;
    hasHotFlagError: boolean = false;
    
    changeDetectionRef = inject(ChangeDetectorRef);
    bctItems: { [key: string]: { vendor: string, hotFlagValue: string } } = {};

    constructor(
        private featureFlagService: FeatureFlagService,
        private store: Store<AppState>,
        private activeModal: NgbActiveModal,
        private fb: FormBuilder,
    ) { }

    ngOnInit() {
        this.store.dispatch(MyCartActions.getHotFlags());
        if(this.openingFrom === 'CART') {
            this.store.dispatch(MyCartActions.getVendorPrices({ vendorCode: null, openingFrom: this.openingFrom }));
        } else {
            this.store.dispatch(MyCartActions.getVendorPrices({ vendorCode: null, openingFrom: this.openingFrom, items: this.requestOrderItems, requestBranchCode: this.requestBranchCode }));
        }
        this.changeDetectionRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.cartVin.unsubscribe();
        this.vinUpdate.unsubscribe();
        this.crcUpdate.unsubscribe();
        this.esnUpdate.unsubscribe();
        if(this.vinControlSub) this.vinControlSub.unsubscribe();
    }

    initForm(selectedCartItems: CartResultLineItemSelection[] | RequestOrderItem[]) {
        if (this.editHotFlagCode) {
            this.lastExpandedIndex = 0;
            this.focusElementId = 'hotFlag_vin_cartItemHotFlag_0';
        }
        const crcValue = this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.crc?.value ?? '';
        const esnValue =
          this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.esn?.value?.length > 0
            ? this.form.controls.cartItems.controls[this.lastExpandedIndex]?.controls?.esn?.value
            : (this.esn ?? '');
        
        const formValue = this.form.value;
        this.form.controls.cartItems.clear();
        if(this.openingFrom === 'CART') {
            (selectedCartItems as CartResultLineItemSelection[]).forEach((ci: CartResultLineItemSelection, index: number) => {
                const hotFlagCode = this.editHotFlagCode && index === 0
                    ? this.editHotFlagCode
                    : ci.hotFlagCode;
                // ESN required for any warranty hot flag (21)
                const isESNRequired = this.isWarranty(hotFlagCode);
                this.form.controls.cartItems.push(this.fb.group({
                    cartItemId: new FormControl<string>(ci.cartItemId),
                    hotFlagCode: new FormControl<string>(hotFlagCode, [Validators.required]),
                    bctId: new FormControl(null),
                    crc: new FormControl<string>(ci?.crc ?? ''),
                    esn: new FormControl<string>(ci?.esn ?? '', isESNRequired ? this.esnValidators : [])
                }));
                if(isESNRequired) {
                    this.setESNError();
                }
            });
        } else {
            (selectedCartItems as RequestOrderItem[]).forEach((roi: RequestOrderItem, index: number) => {
                // Find the previous hot flag value for this specific part by matching bctId instead of relying on array index
                const previousFormItem = formValue.cartItems?.find(item => item.bctId === roi._id);
                const hotFlagCode = (this.editHotFlagCode && index === 0
                    ? this.editHotFlagCode
                    : previousFormItem?.hotFlagCode) ?? roi.hotflag ?? '';
                // ESN required for any :CE warranty hot flag (21)
                const isESNRequired = this.setCumminsWithWarrantyState(index, roi.vendorCode);
                this.form.controls.cartItems.push(this.fb.group({
                    hotFlagCode: new FormControl<string>(hotFlagCode, [Validators.required]),
                    cartItemId: new FormControl<string>(roi._id),
                    bctId: new FormControl<string>(roi._id),
                    crc: new FormControl<string>(crcValue),
                    esn: new FormControl<string>(esnValue, isESNRequired ? this.esnValidators : [])
                }));
            
                if(isESNRequired) {
                    this.setESNError();
                }
            });
        }

         if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){
          
          this.setVinValidators();
          this.setVinTrim();
          this.setVINError(); // Check VIN validation state after setting validators
          this.setHotFlagCodeRequiredError();
        }
        this.form.updateValueAndValidity(); 

        // Initialize VIN control visibility and ESN/CRC control visibility for each cart item after form is ready
        if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){
            this.form.controls.cartItems.controls.forEach((_, index) => {
            const vendorCode = this.openingFrom === 'CART' 
                ? (selectedCartItems as CartResultLineItemSelection[])[index]?.vendorCode 
                : (selectedCartItems as RequestOrderItem[])[index]?.vendor;
            if (vendorCode) {
                this.setEsnValidators(index, vendorCode);
                this.setShowVINControl(index, vendorCode);
                }
            
            });
        }
        if (this.focusElementId) {
            setTimeout(() => document.querySelector<HTMLInputElement>(`#${this.focusElementId}`)?.focus());
        }
    }

    public closeModal(): void {
        this.activeModal.close();
    }

    filter() {
        this.filterSubject.next(this.filterKey);
    };

    removeCartItem(cartItem: CartResultLineItemSelection) {
        if(this.openingFrom === "BCT") {
            this.removedItemBctId = cartItem?.bctId ?? "";
            // Clean up the bctItems to prevent removed parts from being included in hot flag updates
            if (cartItem.bctId && this.bctItems[cartItem.bctId]) {
                delete this.bctItems[cartItem.bctId];
            }
            // For Back Counter requests, we need to uncheck the part in the underlying form
            // Dispatch an action to notify the table component to uncheck this item
            if (cartItem.bctId) {
                this.store.dispatch(BackCounterRequestActions.removePartFromProcurement({
                    partId: cartItem.bctId,
                    orderNumber: this.orderNumber
                }));
            }
        }
        this.store.dispatch(MyCartActions.toggleSelection({ cartItemId: cartItem.cartItemId }));
    }

    sendHotFlags() {
        if(this.openingFrom === 'CART') {
            this.store.dispatch(MyCartActions.sendHotFlag());
        } else {
            const vin = this.form.value.vin;
            const esn = this.form.value.cartItems.find(f => f.esn?.length > 0)?.esn ?? null;
            const crc = this.form.value.cartItems.find(f => f.crc?.length > 0)?.crc ?? null;
            this.store.dispatch(BackCounterRequestActions.updateHotFlagValue({
                orderNumber: this.orderNumber,
                items: this.bctItems,
                esn,
                crc,
                vin
            }));
            this.closeModal();
        }
    }

    onHotFlagChange(index: number, hotFlagValue: string, vendorCode: string) {
       const controls = this.getCartItemControls(index);
       // Update the existing control value instead of creating a new one
       controls.hotFlagCode.setValue(hotFlagValue);

          if(this.featureFlagService.isFeatureActive('Hotflag.VinNumber')){

          this.setVinValidators();
          this.setVinTrim();
          this.setVINError();
        }  

        this.setEsnValidators(index, vendorCode);
        this.setBCTData(controls, vendorCode);
        this.updateCartItemHotFlag(controls, vendorCode);
        this.setShowVINControl(index, vendorCode);
        this.setHotFlagCodeRequiredError();
        
        // Trigger change detection to ensure UI updates
        this.changeDetectionRef.detectChanges();
    }

    setBCTData(controls: CartItemFormGroup, vendorCode: string) {
        if (!controls.hotFlagCode.valid) {
            return;
        }
        const bctId = controls.bctId.value;
        const hotFlagCode = controls.hotFlagCode.value;
        this.bctItems[bctId] = { vendor: vendorCode, hotFlagValue: hotFlagCode };
    }

    setEsnValidators(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const isESNRequired = this.setCumminsWithWarrantyState(index, vendorCode);
        
        if(isESNRequired) {
            this.form.controls.cartItems.controls[index].controls.esn.setValidators(this.esnValidators);
            // Mark as touched to ensure validation styling appears
            this.form.controls.cartItems.controls[index].controls.esn.markAsTouched();
            this.setESNError();
        } else {
            this.form.controls.cartItems.controls[index].controls.esn.clearValidators();
        }
        this.form.controls.cartItems.controls[index].controls.esn.updateValueAndValidity();
    }

    private setVinValidators() {
        const isVINRequired = this.form.controls.cartItems.controls.some((g: FormGroup<CartItemFormGroup>) => this.isVINRequiredForHotFlag(g.controls.hotFlagCode.value));

        if (isVINRequired) {
            this.form.controls.vin.setValidators(this.vinValidators);
            this.form.controls.vin.markAsTouched();
            // Force the field to be dirty to ensure validation is visible immediately
            this.form.controls.vin.markAsDirty();
        } else {
            this.form.controls.vin.clearValidators();
        }

        this.form.controls.vin.updateValueAndValidity();
        this.setVINError();
    }

    setVinTrim() {
        if(this.vinControlSub) {
            this.vinControlSub.unsubscribe();
        }
        this.vinControlSub = this.form.controls['vin'].valueChanges.subscribe(value => {
            const trimmedValue = value?.replace(/\s+/g, '');
            this.form.controls['vin'].setValue(trimmedValue, { emitEvent: false });
        });
    }

    updateCartItemHotFlag(controls: CartItemFormGroup, vendorCode?: string) {
        if (!controls.hotFlagCode.valid) {
            return;
        }

        if (this.openingFrom === 'CART' && (!this.isVINRequiredForHotFlag(controls.hotFlagCode.value) || (this.form.controls.vin.valid))) {
            this.store.dispatch(MyCartActions.updateCartItemHotFlag({
                cartItemId: controls.cartItemId.value,
                hotFlagCode: controls.hotFlagCode.value,
            }));
        }
    }

    setShowVINControl(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        this.showVINControl = (this.isVINRequiredForHotFlag(hotFlagCode) || this.setCumminsWithWarrantyState(index, vendorCode)) && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');
        this.setVINError();
    }

    isVINControlVisible(index: number, vendorCode: string) {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        return (this.isVINRequiredForHotFlag(hotFlagCode) || this.setCumminsWithWarrantyState(index, vendorCode)) && this.featureFlagService.isFeatureActive('Hotflag.VinNumber');
    }

    setCumminsWithWarrantyState(index: number, vendorCode: string): boolean {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const value =  this.isCummins(vendorCode) && this.isWarranty(hotFlagCode);
        
        this.showESNWithCRC = value ;
        
        this.setESNError();
        return value;
    }

    updateCumminsWithWarrantyVisibility(index: number, vendorCode: string): boolean {
        const hotFlagCode = this.form.controls.cartItems.controls[index]?.controls?.hotFlagCode?.value || '';
        const value =  this.isCummins(vendorCode) && this.isWarranty(hotFlagCode);
        return value;
    }

    isCummins(vendorCode: string): boolean {
        return this.CUMMINS_VENDORS.some(s => vendorCode?.includes(s));
    }

    isWarranty(hotFlagCode: string) {
        return this.HOT_FLAG_CODE_WARRANTY === hotFlagCode;
    }

    isVINRequiredForHotFlag(hotFlagCode: string): boolean {
        return this.vinHotFlagCodes.includes(hotFlagCode);
    }

    getCartItemControls(index: number): CartItemFormGroup {
        return this.form.controls.cartItems.controls[index]?.controls;
    }

    setHotFlagCodeRequiredError() {
        this.hasHotFlagError = this.form.controls.cartItems.controls.some(g => g.controls.hotFlagCode.errors?.required);
    }

    setVINError() {
        const minlengthError = this.form.controls.vin.errors?.minlength;
        const requiredError = this.form.controls.vin.errors?.required;
        const patternError = this.form.controls.vin.errors?.pattern;
        this.hasVINError = !!(minlengthError || requiredError || patternError);
        
        // Trigger change detection to ensure the error state is reflected in the UI
        this.changeDetectionRef.detectChanges();
    }

    setESNError() {
        const minlengthError = this.form.controls.cartItems.controls.some(s => s.controls.esn.errors?.minlength);
        const requiredError = this.form.controls.cartItems.controls.some(s => s.controls.esn.errors?.required);
        this.hasESNError =  minlengthError || requiredError;
    }

    accordionItemShown(index: number, vendorCode: string) {
        this.setShowVINControl(index, vendorCode);
        this.setCumminsWithWarrantyState(index, vendorCode);
    }

    
}
